import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';
import { LoginRequest, ForgotPasswordRequest, ResetPasswordRequest } from '../../../models/auth.models';
import { Log, IShortLogger } from '../../../services/logger.service';
import { NotificationsService } from '../../../services/home-services/notifications.service';
import { LoginEntityService } from '../../../services/entity-services/login-entity.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { User } from '../../../models/home/sign-up';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly logger: IShortLogger<LoginComponent> = Log.create(LoginComponent);
  private readonly destroy$ = new Subject<void>();
  private cooldownInterval?: ReturnType<typeof setInterval>;

  // ── Flip state ───────────────────────────────────────────────────────────
  rotation = 0;
  backFaceContent: 'signup' | 'forgot' | null = null;

  // ── Login form ───────────────────────────────────────────────────────────
  loginForm: FormGroup;
  showPassword = false;
  isLoading = false;
  errorMessage = '';
  returnUrl = '/dashboard';
  private hasExplicitReturnUrl = false;

  // ── Sign-up form ─────────────────────────────────────────────────────────
  signupForm: FormGroup;
  isSubmitting = false;
  showSignupPassword = false;
  showConfirmPassword = false;
  serverErrorMessage = '';
  signupEmailChecking = false;
  signupEmailTaken: boolean | null = null; // null=unchecked, true=taken, false=available
  passwordValidation = {
    minLength: false, hasUppercase: false, hasLowercase: false,
    hasNumber: false, hasSpecial: false, passwordsMatch: false
  };

  // ── Forgot-password ──────────────────────────────────────────────────────
  forgotPasswordForm: FormGroup;
  isForgotLoading = false;
  forgotStep: 1 | 2 = 1;
  forgotErrorMessage = '';
  forgotUserExists: boolean | null = null; // null=unchecked, true=found, false=not found
  forgotEmailChecking = false;
  sendCodeCooldown = 0;

  // ── Reset-password (inline step 2 of forgot flow) ────────────────────────
  resetPasswordForm: FormGroup;
  isResetting = false;
  resetErrorMessage = '';
  showResetNewPassword = false;
  showResetConfirmPassword = false;
  resetPasswordValidation = {
    minLength: false, hasUppercase: false, hasLowercase: false,
    hasNumber: false, hasSpecial: false, passwordsMatch: false
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private notificationsService: NotificationsService,
    private loginEntityService: LoginEntityService,
    private snackBar: MatSnackBar,
  ) {
    this.loginForm = this.formBuilder.group({
      loginId: ['', [Validators.required]],
      password: ['', [Validators.required]]
    });

    this.signupForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      loginId: ['', [Validators.required, Validators.email]],
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        this.passwordStrengthValidator
      ]],
      confirmPassword: ['', [Validators.required]],
      details: this.formBuilder.group({
        phone: [''],
        address: [''],
        dateOfBirth: ['']
      })
    }, { validators: this.passwordMatchValidator });

    this.signupForm.get('password')?.valueChanges.subscribe(() => this.validateSignupPassword());
    this.signupForm.get('confirmPassword')?.valueChanges.subscribe(() => this.validateSignupPassword());

    this.forgotPasswordForm = this.formBuilder.group({
      loginId: ['', [Validators.required, Validators.email]]
    });

    // Reset-password form uses same field names so passwordMatchValidator reuses cleanly
    this.resetPasswordForm = this.formBuilder.group({
      otpCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6), Validators.pattern(/^\d{6}$/)]],
      password: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    this.resetPasswordForm.get('password')?.valueChanges.subscribe(() => this.validateResetPassword());
    this.resetPasswordForm.get('confirmPassword')?.valueChanges.subscribe(() => this.validateResetPassword());
  }

  ngOnInit(): void {
    const raw: string = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
    this.hasExplicitReturnUrl = !!this.route.snapshot.queryParams['returnUrl'];
    // Guard against routing loops: never return to /login, the root path,
    // or any wildcard-redirect artifact — default to dashboard instead.
    this.returnUrl = (raw === '/' || raw.startsWith('/login') || raw.includes('**'))
      ? '/dashboard'
      : raw;

    // Immediate clear — reset indicator the moment the user starts typing
    this.forgotPasswordForm.get('loginId')!.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => { this.forgotUserExists = null; this.forgotEmailChecking = false; });

    // Debounced — actual API check once the user pauses
    this.forgotPasswordForm.get('loginId')!.valueChanges.pipe(
      debounceTime(600), distinctUntilChanged(), takeUntil(this.destroy$)
    ).subscribe(email => {
      if (email && this.forgotPasswordForm.get('loginId')!.valid) {
        this.checkForgotUserExists(email);
      }
    });

    // Same pattern for signup email
    this.signupForm.get('loginId')!.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => { this.signupEmailTaken = null; this.signupEmailChecking = false; });

    this.signupForm.get('loginId')!.valueChanges.pipe(
      debounceTime(600), distinctUntilChanged(), takeUntil(this.destroy$)
    ).subscribe(email => {
      if (email && this.signupForm.get('loginId')!.valid) {
        this.checkSignupEmailTaken(email);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.cooldownInterval) clearInterval(this.cooldownInterval);
  }

  // ── Flip navigation ───────────────────────────────────────────────────────

  goToSignup(): void {
    this.signupForm.reset();
    this.serverErrorMessage = '';
    this.signupEmailChecking = false;
    this.signupEmailTaken = null;
    this.backFaceContent = 'signup';
    this.rotation -= 180;
  }

  goToForgot(): void {
    this.forgotPasswordForm.reset();
    // Re-enable in case it was locked after a previous OTP send
    this.forgotPasswordForm.get('loginId')!.enable();
    this.resetPasswordForm.reset();
    this.forgotStep = 1;
    this.forgotErrorMessage = '';
    this.forgotUserExists = null;
    this.forgotEmailChecking = false;
    this.resetErrorMessage = '';
    this.sendCodeCooldown = 0;
    if (this.cooldownInterval) { clearInterval(this.cooldownInterval); this.cooldownInterval = undefined; }
    this.backFaceContent = 'forgot';
    this.rotation += 180;
  }

  // "Back to Login" from signup continues in the same RTL direction (–180 ➜ –360 = full 360°)
  goBackFromSignup(): void {
    this.rotation -= 180;
  }

  // "Back to Login" from forgot continues in the same LTR direction (+180 ➜ +360 = full 360°)
  goBackFromForgot(): void {
    this.rotation += 180;
  }

  // ── Login ────────────────────────────────────────────────────────────────

  get loginId() { return this.loginForm.get('loginId'); }
  get password() { return this.loginForm.get('password'); }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  login(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    const loginRequest: LoginRequest = this.loginForm.value;

    this.authService.login(loginRequest).subscribe({
      next: (authUser) => {
        localStorage.setItem('loginId', authUser.loginId);
        localStorage.setItem('userId', authUser.id);
        localStorage.setItem('userName', authUser.name);
        localStorage.setItem('roles', JSON.stringify(authUser.roles));
        localStorage.setItem('team', authUser.team || '');
        localStorage.setItem('teamId', authUser.teamId || '');
        if (this.hasExplicitReturnUrl) {
          this.router.navigate([this.returnUrl]);
        } else {
          // Redirect to notifications page if there are unread items
          this.notificationsService.getNotifactionsForUsers(authUser.id).subscribe({
            next: (notifications: any[]) => {
              const hasUnread = (notifications ?? []).some((n: any) => !n.isRead);
              this.router.navigate([hasUnread ? '/notifications' : this.returnUrl]);
            },
            error: () => this.router.navigate([this.returnUrl])
          });
        }
      },
      error: (error) => {
        this.logger.e('Login failed:', error);
        this.errorMessage = error.message || 'Invalid credentials. Please try again.';
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  // ── Sign-up ──────────────────────────────────────────────────────────────

  // Static validator — does not reference `this`, safe to use as FormControl validator
  passwordStrengthValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value) return null;
    const valid = /\d/.test(value) && /[A-Z]/.test(value) && /[a-z]/.test(value) &&
                  /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value) && value.length >= 8;
    return valid ? null : { passwordStrength: true };
  }

  // Cross-field validator — does not reference `this`
  passwordMatchValidator(group: AbstractControl): { [key: string]: any } | null {
    const password = group.get('password');
    const confirmPassword = group.get('confirmPassword');
    if (!password || !confirmPassword) return null;
    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  validateSignupPassword(): void {
    const password = this.signupForm.get('password')?.value || '';
    const confirmPassword = this.signupForm.get('confirmPassword')?.value || '';
    this.passwordValidation = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      passwordsMatch: password === confirmPassword && password.length > 0
    };
  }

  isPasswordValid(): boolean {
    const v = this.passwordValidation;
    return v.minLength && v.hasUppercase && v.hasLowercase && v.hasNumber && v.hasSpecial && v.passwordsMatch;
  }

  validateResetPassword(): void {
    const p = this.resetPasswordForm.get('password')?.value || '';
    const c = this.resetPasswordForm.get('confirmPassword')?.value || '';
    this.resetPasswordValidation = {
      minLength: p.length >= 8, hasUppercase: /[A-Z]/.test(p), hasLowercase: /[a-z]/.test(p),
      hasNumber: /\d/.test(p), hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"|,.<>\/?]/.test(p),
      passwordsMatch: p === c && p.length > 0
    };
  }

  isResetPasswordValid(): boolean {
    const v = this.resetPasswordValidation;
    return v.minLength && v.hasUppercase && v.hasLowercase && v.hasNumber && v.hasSpecial && v.passwordsMatch;
  }

  toggleSignupPasswordVisibility(): void { this.showSignupPassword = !this.showSignupPassword; }
  toggleConfirmPasswordVisibility(): void { this.showConfirmPassword = !this.showConfirmPassword; }
  toggleResetNewPasswordVisibility(): void { this.showResetNewPassword = !this.showResetNewPassword; }
  toggleResetConfirmPasswordVisibility(): void { this.showResetConfirmPassword = !this.showResetConfirmPassword; }

  get signupName() { return this.signupForm.get('name'); }
  get signupLoginId() { return this.signupForm.get('loginId'); }
  get signupPassword() { return this.signupForm.get('password'); }
  get signupConfirmPassword() { return this.signupForm.get('confirmPassword'); }
  get signupControls() { return this.signupForm.controls; }

  async onSignupSubmit(): Promise<void> {
    if (this.signupEmailTaken === true) return;
    if (!this.isPasswordValid() || this.signupForm.invalid) {
      this.signupForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.serverErrorMessage = '';
    const formData = this.signupForm.value;
    const email = formData.loginId;

    const newUser = new User();
    newUser.name = formData.name;
    newUser.loginId = email;
    newUser.password = formData.password;
    newUser.roles = ['student'];

    try {
      const createdSignUp = await this.loginEntityService.signUp(newUser);
      this.logger.i('Created SignUp Record:', createdSignUp);
      // Pre-fill the login email and flip back to the login face
      this.loginForm.patchValue({ loginId: email });
      this.signupForm.reset();
      this.snackBar.open('Account created. You can now sign in.', 'OK', { duration: 4000 });
      this.goBackFromSignup();
    } catch (error: any) {
      this.logger.e('Signup failed:', error);
      this.serverErrorMessage = error?.message || 'An error occurred. Please try again.';
    } finally {
      this.isSubmitting = false;
    }
  }

  checkSignupEmailTaken(email: string): void {
    this.signupEmailChecking = true;
    this.signupEmailTaken = null;
    this.loginEntityService.login(email).subscribe({
      next: () => { this.signupEmailChecking = false; this.signupEmailTaken = true; },
      error: (err: any) => {
        this.signupEmailChecking = false;
        // 404 = not found = email available; anything else = couldn't determine
        this.signupEmailTaken = err?.status === 404 ? false : null;
      }
    });
  }

  // ── Forgot password ──────────────────────────────────────────────────────

  get forgotLoginId() { return this.forgotPasswordForm.get('loginId'); }

  checkForgotUserExists(email: string): void {
    this.forgotEmailChecking = true;
    this.forgotUserExists = null;
    this.loginEntityService.login(email).subscribe({
      next: () => { this.forgotEmailChecking = false; this.forgotUserExists = true; },
      error: (err: any) => {
        this.forgotEmailChecking = false;
        this.forgotUserExists = err?.status === 404 ? false : null;
      }
    });
  }

  private startSendCodeCooldown(): void {
    this.sendCodeCooldown = 30;
    if (this.cooldownInterval) clearInterval(this.cooldownInterval);
    this.cooldownInterval = setInterval(() => {
      this.sendCodeCooldown--;
      if (this.sendCodeCooldown <= 0) { clearInterval(this.cooldownInterval); this.cooldownInterval = undefined; }
    }, 1000);
  }

  submitForgotPassword(): void {
    if (this.forgotPasswordForm.invalid || this.sendCodeCooldown > 0) return;
    this.isForgotLoading = true;
    this.forgotErrorMessage = '';

    this.authService.forgotPassword({ LoginId: this.forgotPasswordForm.value.loginId }).subscribe({
      next: () => {
        this.isForgotLoading = false;
        this.forgotStep = 2;
        // Lock the email field so the user can't swap accounts mid-flight
        this.forgotPasswordForm.get('loginId')!.disable();
        this.startSendCodeCooldown();
      },
      error: (error) => {
        this.isForgotLoading = false;
        this.logger.e('Forgot password error:', error);
        this.forgotErrorMessage =
          error.error?.message || error.error?.title || error.message ||
          'An error occurred. Please try again.';
      }
    });
  }

  // ── Reset password (inline step 2) ───────────────────────────────────────

  submitResetPassword(): void {
    if (this.resetPasswordForm.invalid || !this.isResetPasswordValid()) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    this.isResetting = true;
    this.resetErrorMessage = '';
    // Use control.value directly — disabled controls are excluded from form.value
    const email = this.forgotPasswordForm.get('loginId')?.value;
    const formValue = this.resetPasswordForm.value;

    const request: ResetPasswordRequest = {
      LoginId: email,
      OtpCode: formValue.otpCode,
      NewPassword: formValue.password,
      ConfirmPassword: formValue.confirmPassword
    };

    this.authService.resetPassword(request).subscribe({
      next: () => {
        this.isResetting = false;
        // Pre-fill the login email field and flip back to the sign-in face
        this.loginForm.patchValue({ loginId: email });
        this.resetPasswordForm.reset();
        this.snackBar.open('Password reset! You can now sign in.', 'OK', { duration: 4000 });
        this.goBackFromForgot();
      },
      error: (error) => {
        this.isResetting = false;
        this.resetErrorMessage = error.message || 'Invalid or expired code. Please try again.';
      }
    });
  }
}
